const UPLOAD_RANGE_PATTERN = /^bytes=0-(\d+)$/

const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024
const CHUNK_ATTEMPTS = 3

async function responseError(response, fallback) {
  let detail = ''
  try {
    detail = String(await response.text()).trim()
  } catch {
    // The status is still sufficient when the storage response has no readable body.
  }
  return Object.assign(new Error(detail || `${fallback} (${response.status})`), {
    nonRetryable: response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429,
  })
}

function abortIfNeeded(signal) {
  if (signal?.aborted) throw new DOMException('Upload canceled', 'AbortError')
}

async function initiateStorageSession(session, fetchImpl, signal) {
  const response = await fetchImpl(session.url, {
    method: session.method || 'POST',
    headers: session.headers || {},
    ...(signal ? { signal } : {}),
  })
  abortIfNeeded(signal)
  if (!response.ok) throw await responseError(response, 'Failed to initiate upload')
  const location = response.headers.get('Location')
  if (!location) throw new Error('Upload storage did not return a resumable session')
  return location
}

function acknowledgedOffset(response) {
  const range = response.headers.get('Range')
  if (!range) return 0
  const match = range.match(UPLOAD_RANGE_PATTERN)
  return match ? Number(match[1]) + 1 : 0
}

async function queryUploadOffset(fetchImpl, sessionUrl, total, signal) {
  const response = await fetchImpl(sessionUrl, {
    method: 'PUT',
    headers: { 'Content-Range': `bytes */${total}` },
    ...(signal ? { signal } : {}),
  })
  if (response.ok) return total
  if (response.status === 308) return acknowledgedOffset(response)
  throw await responseError(response, 'Failed to query media upload progress')
}

async function uploadChunk(fetchImpl, sessionUrl, file, start, chunkSize, options) {
  const total = file.size
  const endExclusive = Math.min(start + chunkSize, total)
  const chunk = file.slice(start, endExclusive, file.type)
  let lastError = null
  for (let attempt = 0; attempt < CHUNK_ATTEMPTS; attempt += 1) {
    abortIfNeeded(options.signal)
    try {
      const response = await fetchImpl(sessionUrl, {
        method: 'PUT',
        headers: {
          'Content-Range': `bytes ${start}-${endExclusive - 1}/${total}`,
        },
        body: chunk,
        ...(options.signal ? { signal: options.signal } : {}),
      })
      abortIfNeeded(options.signal)
      if (response.ok) return total
      if (response.status === 308) {
        const offset = acknowledgedOffset(response)
        if (offset > start && offset <= endExclusive) return offset
        throw Object.assign(new Error('Upload storage acknowledged an invalid byte range'), { nonRetryable: true })
      }
      const error = await responseError(response, 'Failed to upload media chunk')
      if (response.status !== 408 && response.status !== 429 && response.status < 500) {
        error.nonRetryable = true
        throw error
      }
      lastError = error
    } catch (error) {
      abortIfNeeded(options.signal)
      if (error?.nonRetryable) throw error
      lastError = error
    }
    options.onRetry?.()
    try {
      const offset = await queryUploadOffset(fetchImpl, sessionUrl, total, options.signal)
      if (offset > start) return offset
    } catch (error) {
      abortIfNeeded(options.signal)
      lastError = error
    }
  }
  throw lastError || new Error('Failed to upload media chunk')
}

export async function transferResumableFile(file, session, onProgress = () => {}, fetchImpl = fetch, options = {}) {
  if (!file || file.size <= 0) throw new Error('Cannot upload an empty file')
  if (!session?.url) throw new Error('Upload session is missing its storage URL')
  abortIfNeeded(options.signal)
  const reportProgress = (loaded) => {
    onProgress(Math.floor((loaded / file.size) * 100))
    options.onBytesProgress?.({ loaded, total: file.size })
  }

  if ((session.method || '').toUpperCase() === 'PUT') {
    const response = await fetchImpl(session.url, {
      method: 'PUT',
      headers: session.headers || {},
      body: file,
      ...(options.signal ? { signal: options.signal } : {}),
    })
    abortIfNeeded(options.signal)
    if (!response.ok) throw await responseError(response, 'Failed to upload media')
    reportProgress(file.size)
    return
  }

  const sessionUrl = session.resume_url || await initiateStorageSession(session, fetchImpl, options.signal)
  options.onStorageSession?.(sessionUrl)
  const chunkSize = Math.max(256 * 1024, Number(session.chunk_size_bytes) || DEFAULT_CHUNK_SIZE)
  let uploaded = session.resume_url ? await queryUploadOffset(fetchImpl, sessionUrl, file.size, options.signal) : 0
  if (!Number.isSafeInteger(uploaded) || uploaded < 0 || uploaded > file.size) {
    throw Object.assign(new Error('Upload storage returned an invalid progress offset'), { nonRetryable: true })
  }
  reportProgress(uploaded)
  while (uploaded < file.size) {
    const nextOffset = await uploadChunk(fetchImpl, sessionUrl, file, uploaded, chunkSize, options)
    if (nextOffset <= uploaded || nextOffset > file.size) {
      throw Object.assign(new Error('Upload storage returned an invalid progress offset'), { nonRetryable: true })
    }
    uploaded = nextOffset
    reportProgress(uploaded)
  }
}

export function mediaUploadRequest(file, fields = {}) {
  return {
    filename: file.name,
    title: fields.title || file.name,
    asset_type: fields.asset_type,
    content_type: fields.content_type || file.type,
    size_bytes: file.size,
    save_to_library: fields.save_to_library,
    purpose: fields.purpose,
  }
}
